import {
	ApiService,
	useAuthStore,
	usePreferencesStore,
} from '@supply-mover-2-0/core';
import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import type { Theme, ThemeName } from '../services/ThemeService';
import { themeService } from '../services/ThemeService';
import { darkTheme } from '../themes/dark-theme';
import { lightTheme } from '../themes/light-theme';
import { applyThemeVariables } from '../utils/theme-utils';

interface ThemeContextValue {
	theme: Theme;
	setTheme: (themeName: ThemeName) => void;
	toggleTheme: () => void;
	isDark: boolean;
	colorScheme: 'light' | 'dark';
	toggleColorScheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
	children: React.ReactNode;
	initialTheme?: ThemeName;
	/**
	 * Lock to a specific theme, skipping the usual OS-preference / prefs-store /
	 * DB resolution AND the system-theme listener. Used by the embeddable widget
	 * so a third-party page renders one deterministic look. CRM omits this and
	 * keeps the dynamic resolution.
	 */
	forceTheme?: ThemeName;
	/**
	 * When false, do NOT write theme CSS variables or `light`/`dark` classes onto
	 * the host `document` root/body. The embeddable widget sets this false and
	 * scopes its vars to its own shadow root instead (so it never mutates the
	 * page it's embedded in). Defaults true to preserve crm-web behavior.
	 */
	applyToDocument?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
	children,
	initialTheme = 'light',
	forceTheme,
	applyToDocument = true,
}) => {
	const [theme, setThemeState] = useState<Theme>(() => {
		// Embed path: lock to the forced theme, no OS/prefs/DB resolution.
		if (forceTheme != null) {
			themeService.setTheme(forceTheme);
			return themeService.getCurrentTheme();
		}

		let themeToUse: ThemeName;

		// Check preferences store first for saved preference
		if (typeof window !== 'undefined') {
			const savedTheme = usePreferencesStore.getState().theme;

			if (savedTheme === 'dark' || savedTheme === 'light') {
				themeToUse = savedTheme;
			} else if (window.matchMedia) {
				// Fall back to system preference
				const prefersDarkMode = window.matchMedia(
					'(prefers-color-scheme: dark)',
				).matches;
				themeToUse = prefersDarkMode ? 'dark' : 'light';
			} else {
				themeToUse = 'dark';
			}
		} else {
			themeToUse = 'dark';
		}

		if (themeToUse !== 'light') {
			themeService.setTheme(themeToUse);
		}

		return themeService.getCurrentTheme();
	});

	// Subscribe to auth state to detect login/logout
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const accessToken = useAuthStore((state) => state.accessToken);
	const isLoading = useAuthStore((state) => state.isLoading);
	const isAuthInitialized = useAuthStore((state) => state.isAuthInitialized);

	// Track if we've fetched from DB to avoid duplicate fetches
	const hasFetchedFromDb = useRef(false);

	// Reset fetch flag when user logs out so we can re-fetch on next login
	useEffect(() => {
		if (!isAuthenticated) {
			hasFetchedFromDb.current = false;
		}
	}, [isAuthenticated]);

	useEffect(() => {
		// Subscribe to theme changes
		const unsubscribe = themeService.subscribe((newTheme) => {
			setThemeState(newTheme);

			// Update document class for Tailwind dark mode (skipped for embeds,
			// which scope their theme to a shadow root instead of the page).
			if (typeof window !== 'undefined' && applyToDocument) {
				const root = document.documentElement;
				const body = document.body;
				root.classList.remove('light', 'dark');
				root.classList.add(newTheme.id);
				body.classList.remove('light', 'dark');
				body.classList.add(newTheme.id);

				// Apply CSS variables from the new theme format
				const themeToApply = newTheme.id === 'dark' ? darkTheme : lightTheme;

				if (themeToApply.cssVars) {
					applyThemeVariables(themeToApply.cssVars);
				}

				// Save to preferences store for persistence
				usePreferencesStore
					.getState()
					.setTheme(newTheme.id as 'light' | 'dark');

				// Save to DB only if authenticated (fire and forget, non-blocking)
				const authState = useAuthStore.getState();
				if (
					authState.isAuthenticated &&
					authState.accessToken != null &&
					!authState.isLoading &&
					authState.isAuthInitialized
				) {
					ApiService.apiPut('/language/user/theme', {
						theme: newTheme.id,
					}).catch(() => {
						// Silently fail - localStorage is fallback
					});
				}
			}
		});

		// Set initial theme class and CSS variables on the document (skipped for
		// embeds — they inject their vars into their own shadow root instead).
		if (typeof window !== 'undefined' && applyToDocument) {
			const root = document.documentElement;
			const body = document.body;
			root.classList.remove('light', 'dark');
			root.classList.add(theme.id);
			body.classList.remove('light', 'dark');
			body.classList.add(theme.id);

			// Apply initial CSS variables
			const initialTheme = theme.id === 'dark' ? darkTheme : lightTheme;
			if (initialTheme.cssVars) {
				applyThemeVariables(initialTheme.cssVars);
			}
		}

		// A forced theme (embed) is fixed — no DB sync, no OS-preference listener.
		if (forceTheme == null && typeof window !== 'undefined') {
			// Fetch theme from DB on login (once per session)
			const fetchThemeFromDb = async () => {
				if (
					hasFetchedFromDb.current ||
					!isAuthenticated ||
					accessToken == null ||
					isLoading ||
					!isAuthInitialized
				) {
					return;
				}

				hasFetchedFromDb.current = true;

				try {
					const response = await ApiService.apiGet<{ theme: string | null }>(
						'/language/user/theme',
					);

					if (response.theme === 'dark' || response.theme === 'light') {
						themeService.setTheme(response.theme);
						usePreferencesStore.getState().setTheme(response.theme);
					}
				} catch (error) {
					// Fall back to localStorage/OS preference (already handled)
				}
			};

			void fetchThemeFromDb();

			// Listen for system theme changes during the session
			const handleSystemThemeChange = (e: MediaQueryListEvent) => {
				// Only follow system preference if user hasn't set a preference
				const savedTheme = usePreferencesStore.getState().theme;

				if (savedTheme == null) {
					const newTheme = e.matches ? 'dark' : 'light';
					themeService.setTheme(newTheme);
				}
			};

			if (window.matchMedia) {
				const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
				// Use addEventListener for modern browsers
				if (mediaQuery.addEventListener) {
					mediaQuery.addEventListener('change', handleSystemThemeChange);
				} else {
					// Fallback for older browsers
					mediaQuery.addListener(handleSystemThemeChange);
				}

				// Cleanup function
				return () => {
					unsubscribe();
					if (mediaQuery.removeEventListener) {
						mediaQuery.removeEventListener('change', handleSystemThemeChange);
					} else {
						mediaQuery.removeListener(handleSystemThemeChange);
					}
				};
			}
		}

		return unsubscribe;
	}, [isAuthenticated, accessToken, isLoading, isAuthInitialized]);

	const setTheme = (themeName: ThemeName) => {
		themeService.setTheme(themeName);
	};

	const toggleTheme = () => {
		themeService.toggleTheme();
	};

	const toggleColorScheme = () => {
		themeService.toggleTheme();
	};

	const isDark = themeService.isDark();
	const colorScheme = theme.id as 'light' | 'dark';

	const contextValue: ThemeContextValue = {
		theme,
		setTheme,
		toggleTheme,
		isDark,
		colorScheme,
		toggleColorScheme,
	};

	return (
		<ThemeContext.Provider value={contextValue}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within ThemeProvider');
	}
	return context;
};

export const useThemeColors = () => {
	const { theme } = useTheme();
	return themeService.getColors();
};

export const useColorScheme = () => {
	const { colorScheme, toggleColorScheme, setTheme } = useTheme();

	return {
		colorScheme,
		setColorScheme: (scheme: 'light' | 'dark') => {
			setTheme(scheme);
		},
		toggleColorScheme,
	};
};
